import type {
  ExternalTraining,
  ExternalTrainingPayload,
  ExternalTrainingResponse,
  GetExternalTrainingBriefsResponse,
  GetMyExternalTrainingsResponse,
  SpendFile,
  SpendFileResponse,
  SpendFileType,
  SpendSignedUrlResponse,
  SpendUploadTicketResponse,
  GetMyTrainingResponse,
  GetMyTrainingsResponse,
  GetMyTrainingSummaryResponse,
  GetTrainingHistoryResponse,
  TrainingHeartbeatPayload,
  TrainingHeartbeatResponse,
  TrainingQuizAttemptPayload,
  TrainingQuizAttemptResponse,
  TrainingScormProgressResponse,
  TrainingSlideProgressResponse,
} from '@gokrew/models/training';
import api from './client';

const endpoints = {
  list: '/employee/trainings',
  summary: '/employee/trainings/summary',
  one: (id: string) => `/employee/trainings/${id}`,
  start: (id: string) => `/employee/trainings/${id}/start`,
  heartbeat: (id: string, chapterId: string) =>
    `/employee/trainings/${id}/chapters/${chapterId}/heartbeat`,
  slides: (id: string, chapterId: string) =>
    `/employee/trainings/${id}/chapters/${chapterId}/slides/progress`,
  scorm: (id: string, chapterId: string) =>
    `/employee/trainings/${id}/chapters/${chapterId}/scorm/progress`,
  attempt: (id: string, quizKey: string) =>
    `/employee/trainings/${id}/quizzes/${quizKey}/attempts`,
};

/** My trainings: to do (overdue first), then completed. */
export async function getMyTrainings() {
  const { data } = await api.get<GetMyTrainingsResponse>(endpoints.list);
  return data.data;
}

/** Counts for the nav badge and the dashboard. */
export async function getMyTrainingSummary() {
  const { data } = await api.get<GetMyTrainingSummaryResponse>(
    endpoints.summary
  );
  return data.data;
}

/** One training: the pinned version, signed media and my progress. */
export async function getMyTraining(id: string) {
  const { data } = await api.get<GetMyTrainingResponse>(endpoints.one(id));
  return data.data;
}

export async function startMyTraining(id: string) {
  await api.post(endpoints.start(id));
}

/** Video progress; the server answers the furthest point allowed. */
export async function sendTrainingHeartbeat(
  id: string,
  chapterId: string,
  payload: TrainingHeartbeatPayload
) {
  const { data } = await api.post<TrainingHeartbeatResponse>(
    endpoints.heartbeat(id, chapterId),
    payload,
    // The player re-syncs on its own; a refused beat needs no toast.
    { quietStatuses: [409] }
  );
  return data.data;
}

/** The slide page now shown (one new page at a time). */
export async function sendTrainingSlideProgress(
  id: string,
  chapterId: string,
  page: number
) {
  const { data } = await api.post<TrainingSlideProgressResponse>(
    endpoints.slides(id, chapterId),
    { page },
    // The viewer's countdown already explains a page that is too soon.
    { quietStatuses: [409] }
  );
  return data.data;
}

/** Where the sandboxed course frame loads a signed launch path from. */
export const scormContentUrl = (launchPath: string) =>
  `${api.defaults.baseURL ?? ''}/${launchPath}`;

/**
 * The CMI a SCORM course reported. The server decides completion from the
 * status strings in it, never from a flag we send.
 */
export async function sendTrainingScormProgress(
  id: string,
  chapterId: string,
  cmi: Record<string, string>
) {
  const { data } = await api.post<TrainingScormProgressResponse>(
    endpoints.scorm(id, chapterId),
    { cmi },
    // A refused report (closed assignment, locked chapter) needs no toast.
    { quietStatuses: [409] }
  );
  return data.data;
}

export async function submitTrainingQuiz(
  id: string,
  quizKey: string,
  payload: TrainingQuizAttemptPayload
) {
  const { data } = await api.post<TrainingQuizAttemptResponse>(
    endpoints.attempt(id, quizKey),
    payload
  );
  return data.data;
}

/** My certificate (PDF), named as the server names it (D114). */
export async function downloadMyAttestation(
  id: string
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get<Blob>(`${endpoints.one(id)}/attestation`, {
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] as
    | string
    | undefined;
  const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1];
  return { blob: response.data, filename: filename ?? 'certificate.pdf' };
}

/** Manager: trainings of my reporting subtree (no answers, D111). */
export async function getTeamTrainings() {
  const { data } = await api.get<GetTrainingHistoryResponse>(
    '/employee/my-team/trainings'
  );
  return data.data;
}

/** Manager: one team member's trainings. */
export async function getMemberTrainings(employeeId: string) {
  const { data } = await api.get<GetTrainingHistoryResponse>(
    `/employee/my-team/members/${employeeId}/trainings`
  );
  return data.data;
}

// ─── External trainings (PR 4.5, D123) ──────────────────────────────────────

const external = (id?: string) =>
  `/employee/trainings/external${id ? `/${id}` : ''}`;

/** My external entries, whether I am in Quebec, and the company currency. */
export async function getMyExternalTrainings() {
  const { data } = await api.get<GetMyExternalTrainingsResponse>(external());
  return data.data;
}

export async function submitExternalTraining(
  payload: ExternalTrainingPayload
): Promise<ExternalTraining> {
  const { data } = await api.post<ExternalTrainingResponse>(
    external(),
    payload
  );
  return data.data;
}

/** Edit while pending or rejected; a rejected one goes back to pending. */
export async function updateExternalTraining(
  id: string,
  payload: ExternalTrainingPayload
): Promise<ExternalTraining> {
  const { data } = await api.put<ExternalTrainingResponse>(
    external(id),
    payload
  );
  return data.data;
}

/** Withdraw while pending. */
export async function withdrawExternalTraining(id: string) {
  await api.delete(external(id));
}

/** PUTs straight to S3; a non-2xx answer is an error. */
async function putToS3(url: string, file: File, contentType: string) {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) throw new Error(`upload failed (${response.status})`);
}

/** Presign, upload, then record a receipt or the attestation. */
export async function uploadExternalTrainingFile(
  id: string,
  file: File,
  kind: SpendFile['kind']
): Promise<SpendFile> {
  const meta = {
    fileName: file.name,
    contentType: (file.type || 'application/pdf') as SpendFileType,
    sizeBytes: file.size,
    kind,
  };
  const { data: ticket } = await api.post<SpendUploadTicketResponse>(
    `${external(id)}/files/upload-url`,
    meta
  );
  await putToS3(ticket.data.url, file, meta.contentType);
  const { data } = await api.post<SpendFileResponse>(`${external(id)}/files`, {
    ...meta,
    key: ticket.data.key,
  });
  return data.data;
}

export async function getExternalTrainingFileUrl(id: string, fileId: string) {
  const { data } = await api.get<SpendSignedUrlResponse>(
    `${external(id)}/files/${fileId}`
  );
  return data.data.url;
}

export async function removeExternalTrainingFile(id: string, fileId: string) {
  await api.delete(`${external(id)}/files/${fileId}`);
}

/** Manager: a member's approved external trainings, without costs (X6). */
export async function getMemberExternalTrainings(employeeId: string) {
  const { data } = await api.get<GetExternalTrainingBriefsResponse>(
    `/employee/my-team/members/${employeeId}/external-trainings`
  );
  return data.data;
}
